import { z } from 'zod';
import { idSchema, slugSchema } from './common.js';
import { subjectSchema } from './content.js';
import { sceneAnswerSchema, verdictSchema } from './progress.js';
/**
 * A learner's onboarding answers, the path they produce, and the versioned config that
 * maps topics to content — ONB-02. `docs/decisions/onboarding-api.md`.
 *
 * Not in the package barrel — import `@platform/schema/onboarding`, for the reason
 * `feedback.ts` gives.
 */
export const onboardingGoalSchema = z
    .enum([
    'CURRENT_JOB',
    'NEXT_ROLE',
    'CAREER_CHANGE',
    'GROW_BUSINESS',
    'LEAD_PEOPLE',
    'UNDERSTAND_BUSINESS',
    'MONEY_DECISIONS',
    'SOMETHING_ELSE',
    'EXPLORING',
])
    .describe('What the learner wants their learning to help them do.');
export const workContextSchema = z
    .enum([
    'STUDENT',
    'EARLY_CAREER',
    'PROFESSIONAL',
    'MANAGER',
    'DEPARTMENT_HEAD',
    'EXECUTIVE',
    'FOUNDER',
    'FREELANCER',
    'BETWEEN_ROLES',
    'SOMETHING_ELSE',
])
    .describe('The work-context card closest to the learner right now.');
export const familiaritySchema = z
    .enum(['NEW', 'BASICS', 'SOMETIMES', 'OFTEN'])
    .describe('How familiar the learner says they are with one topic.');
/** The screens that save an answer or mark progress, in the flow's order. */
export const onboardingStepSchema = z
    .enum([
    'WELCOME',
    'THEME',
    'MEET_LEO',
    'GOAL',
    'INTERESTS',
    'STARTING_FOCUS',
    'WORK_CONTEXT',
    'FAMILIARITY',
    'DAILY_PACE',
    'STREAK_INTRO',
    'STREAK_GOAL',
    'PATH',
    'FIRST_CHALLENGE',
    'COMPLETION',
    'REMINDER',
    'ACCOUNT',
    'HOME',
])
    .describe('The last step the learner finished, so an interrupted onboarding resumes after it. `HOME` means onboarding is complete.');
export const startingTopicMethodSchema = z
    .enum(['CHOSEN', 'RECOMMENDED', 'SINGLE_INTEREST'])
    .describe('How the starting topic was decided: picked on the starting-focus screen, the only interest, or `RECOMMENDED` for a profile saved by the retired "Choose for me".');
export const startingTopicChoiceSchema = subjectSchema.describe('A selected, ready topic. Omitted with one interest, that interest is the start when it is ready.');
const topicFamiliaritySchema = z.object({
    topic: subjectSchema,
    level: familiaritySchema,
});
export const reminderSchema = z
    .object({
    time: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .describe('Local time, `HH:MM`, 24-hour.'),
    timezone: z
        .string()
        .min(1)
        .max(64)
        .describe('IANA timezone, such as `Asia/Jerusalem`.'),
})
    .describe('A daily reminder time in the learner’s timezone.');
/** The spec's lengths for the two free-text answers; the screens cap their fields to these. */
export const GOAL_TEXT_MAX = 60;
/** The most goals a learner can pick; a flow's GOAL screen can lower it. */
export const GOALS_MAX = 3;
export const ROLE_TITLE_MAX = 80;
function unique(values) {
    return new Set(values).size === values.length;
}
export const onboardingAnswersSchema = z
    .strictObject({
    step: onboardingStepSchema,
    goals: z
        .array(onboardingGoalSchema)
        .max(GOALS_MAX)
        .refine(unique, 'Each goal once.')
        .refine((goals) => !goals.includes('EXPLORING') || goals.length === 1, '`EXPLORING` is "I’m just exploring", so it is never beside a goal.')
        .optional()
        .describe('The picked goals, in the order picked. `[EXPLORING]` is "I’m just exploring".'),
    goalText: z
        .string()
        .trim()
        .max(GOAL_TEXT_MAX)
        .optional()
        .describe('Free text beside "Something else". User data: never analytics.'),
    interests: z
        .array(subjectSchema)
        .max(subjectSchema.options.length)
        .refine(unique, 'Each topic once.')
        .optional(),
    startingTopic: startingTopicChoiceSchema.optional(),
    workContext: workContextSchema.optional(),
    roleTitle: z.string().trim().max(ROLE_TITLE_MAX).optional(),
    familiarity: z
        .array(topicFamiliaritySchema)
        .max(subjectSchema.options.length)
        .refine((entries) => unique(entries.map((entry) => entry.topic)), 'Each topic once.')
        .optional(),
    dailyTarget: z
        .int()
        .min(1)
        .max(3)
        .optional()
        .describe('Lessons a day: 1, 2 or 3. Defaults to 1.'),
    reminder: reminderSchema
        .nullable()
        .optional()
        .describe('Omit to keep the reminder already saved — "Not now" on a new phone keeps the account’s. `null` turns it off.'),
})
    .describe('Everything onboarding asks, as far as the learner got. Sent whole each time; saving replaces the previous answers and never touches progress.');
export const pathLessonSchema = z.object({
    slug: slugSchema,
    title: z.string(),
    topic: subjectSchema,
    estimatedMinutes: z.int(),
});
export const pathChallengeSchema = pathLessonSchema
    .omit({ topic: true })
    .describe('The built-in first challenge: the same for every learner, in no topic.');
export const onboardingPathSchema = z
    .object({
    firstChallenge: pathChallengeSchema,
    lessons: z
        .array(pathLessonSchema)
        .describe('What follows the first challenge, in order: the starting topic, then the other ready interests. Empty while no interest is ready.'),
})
    .describe('Ordered references to published lessons.');
export const onboardingProfileSchema = z.object({
    step: onboardingStepSchema,
    goals: z.array(onboardingGoalSchema),
    goalText: z.string().nullable(),
    interests: z.array(subjectSchema),
    startingTopic: subjectSchema.nullable(),
    startingTopicMethod: startingTopicMethodSchema.nullable(),
    workContext: workContextSchema.nullable(),
    roleTitle: z.string().nullable(),
    familiarity: z.array(topicFamiliaritySchema),
    dailyTarget: z.int().min(1).max(3),
    reminder: reminderSchema.nullable(),
});
export const onboardingSchema = z
    .object({
    profile: onboardingProfileSchema,
    suggestedStartingTopic: subjectSchema
        .nullable()
        .describe('The topic the starting-focus screen preselects: the best ready interest for the goals, else the best ready topic of all. Null when nothing is ready.'),
    path: onboardingPathSchema
        .nullable()
        .describe('Null only while the built-in first challenge is not published. A learner with no ready topic still gets one.'),
})
    .describe('The answers as saved, and the path they produce.');
/**
 * One topic a learner can start with, and the lessons that open it. A stored start from
 * before ONB-18 also names a per-topic first challenge; the key is dropped on read.
 */
export const topicStartSchema = z.object({
    topic: subjectSchema,
    next: z
        .array(slugSchema)
        .min(1)
        .max(20)
        .describe('The topic’s opening lessons, in order, after the first challenge.'),
});
export const goalSuggestionSchema = z.object({
    goal: onboardingGoalSchema,
    topics: z.array(subjectSchema).min(1).max(5),
});
/** The longest rewording, a little over the longest English onboarding string. */
const COPY_TEXT_MAX = 300;
export const copyEntrySchema = z.object({
    key: z
        .string()
        .regex(/^[A-Za-z0-9_.]+$/)
        .max(80)
        .describe('A key from `GET /authoring/onboarding/copy`, such as `welcome.title`.'),
    text: z
        .string()
        .trim()
        .min(1)
        .max(COPY_TEXT_MAX)
        .describe('The words that replace the app’s own, with every `{{value}}` kept.'),
});
const onboardingCopySchema = z
    .array(copyEntrySchema)
    .max(500)
    .refine((rows) => unique(rows.map((row) => row.key)), 'Each key once.');
/** The screens the app ships, each once in a flow, in the order it ships them. */
export const builtInStepSchema = onboardingStepSchema.exclude(['HOME']);
/** An admin-added step's id: kept when the step is renamed, moved or reworded. */
const customStepIdSchema = z.string().regex(/^custom-[a-z0-9]{4,24}$/);
/** An uploaded image is held inline, so this caps what every guest downloads. */
export const ONBOARDING_IMAGE_MAX = 200_000;
export const stepArtSchema = z
    .discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('NONE') }),
    z.strictObject({
        kind: z.literal('IMAGE'),
        uri: z
            .string()
            .max(ONBOARDING_IMAGE_MAX)
            .regex(/^(https:\/\/|data:image\/(png|jpeg|webp|svg\+xml)[;,])/)
            .describe('A catalog image URL, another https URL, or an upload.'),
        size: z.enum(['small', 'medium', 'large']).default('medium'),
        fit: z.enum(['contain', 'cover']).default('contain'),
        description: z
            .string()
            .trim()
            .max(COPY_TEXT_MAX)
            .optional()
            .describe('What the image shows, read aloud.'),
        decorative: z
            .boolean()
            .default(false)
            .describe('Says nothing a learner needs, so it is not read aloud.'),
    }),
])
    .describe("Stands in for the screen's illustration. Absent keeps the screen's own.");
/** A drawing is 2–5 KB; this is the scene-image rules' own ceiling. */
const DRAWING_MAX = 32_000;
export const drawOnboardingArtSchema = z
    .strictObject({
    screen: z
        .string()
        .trim()
        .min(1)
        .max(COPY_TEXT_MAX)
        .describe('What the screen is for, which the image has to suit.'),
    prompt: z.string().trim().min(1).max(2000),
    refine: z
        .strictObject({
        drawing: z
            .string()
            .min(1)
            .max(DRAWING_MAX)
            .describe('The candidate to change, as this endpoint returned it.'),
        feedback: z.string().trim().min(1).max(2000),
    })
        .optional(),
})
    .describe('Draw a candidate visual for one onboarding screen.');
export const onboardingArtCandidateSchema = z
    .object({
    uri: z
        .string()
        .nullable()
        .describe('What the step art `uri` holds once applied: an SVG data URI. Null when refused.'),
    drawing: z
        .string()
        .nullable()
        .describe('The drawing to send back as `refine.drawing`.'),
    refusal: z
        .string()
        .nullable()
        .describe('Why nothing can be applied. The current visual is kept.'),
    warning: z
        .string()
        .nullable()
        .describe('What the candidate does not do of what was asked.'),
})
    .describe('A candidate visual, stored nowhere until applied to the draft.');
/** The answers a step can be conditional on: a fixed list, each with its own values. */
export const stepConditionSchema = z
    .discriminatedUnion('answer', [
    z.strictObject({
        answer: z.literal('goal'),
        anyOf: z.array(onboardingGoalSchema).min(1),
    }),
    z.strictObject({
        answer: z.literal('workContext'),
        anyOf: z.array(workContextSchema).min(1),
    }),
])
    .describe('Shown only to a learner whose answer is one of these.');
const stepBase = {
    enabled: z.boolean().default(true),
    showIf: stepConditionSchema.optional(),
    art: stepArtSchema.optional(),
};
const addedText = z.string().trim().min(1).max(COPY_TEXT_MAX);
export const flowStepSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('BUILT_IN'),
        id: builtInStepSchema,
        ...stepBase,
        limit: z
            .literal([1, 2, 3])
            .optional()
            .describe('How many goals a learner can pick. GOAL only; absent is three.'),
        order: z
            .array(z.string())
            .optional()
            .describe('Its answer options by id, in display order. Absent is the app’s order.'),
    }),
    z.strictObject({
        kind: z.literal('INFO'),
        id: customStepIdSchema,
        ...stepBase,
        name: addedText.describe('What the step list calls it.'),
        title: addedText,
        body: addedText.optional(),
        button: addedText,
    }),
    z.strictObject({
        kind: z.literal('QUESTION'),
        id: customStepIdSchema,
        ...stepBase,
        name: addedText.describe('What the step list calls it.'),
        title: addedText,
        body: addedText.optional(),
        options: z
            .array(z.strictObject({
            id: z.string().regex(/^[a-z0-9-]{1,40}$/),
            label: addedText,
        }))
            .max(12),
        multiple: z.boolean().default(false),
        optional: z.boolean().default(false),
        button: addedText,
        skipLabel: addedText.optional(),
    }),
]);
export const onboardingFlowSchema = z
    .array(flowStepSchema)
    .max(60)
    .refine((steps) => unique(steps.map((step) => step.id)), 'Each step once.')
    .refine((steps) => builtInStepSchema.options.every((id) => steps.some((step) => step.id === id)), 'Every built-in screen, disabled or not.')
    .describe('Every onboarding screen in order. Built-in screens can be disabled but never removed, so their ids stay stable.');
export const DEFAULT_ONBOARDING_FLOW = builtInStepSchema.options.map((id) => ({
    kind: 'BUILT_IN',
    id,
    enabled: true,
}));
/**
 * A flow saved before a built-in screen shipped, with that screen added after the
 * built-in one it follows in the app's order. A flow with every screen is returned as is.
 */
export function withBuiltIns(flow) {
    const steps = [...flow];
    builtInStepSchema.options.forEach((id, index) => {
        if (steps.some((step) => step.id === id))
            return;
        const before = builtInStepSchema.options[index - 1];
        const at = steps.findIndex((step) => step.id === before);
        steps.splice(at + 1, 0, { kind: 'BUILT_IN', id, enabled: true });
    });
    return steps;
}
export const onboardingConfigSchema = z
    .strictObject({
    starts: z
        .array(topicStartSchema)
        .max(subjectSchema.options.length)
        .refine((starts) => unique(starts.map((start) => start.topic)), 'Each topic once.'),
    suggestions: z
        .array(goalSuggestionSchema)
        .max(onboardingGoalSchema.options.length)
        .refine((rows) => unique(rows.map((row) => row.goal)), 'Each goal once.'),
    copy: onboardingCopySchema
        .default([])
        .describe('Rewordings of onboarding screens, keyed as `GET /authoring/onboarding/copy` lists them. A key left out shows the app’s own words.'),
    flow: onboardingFlowSchema
        .default(DEFAULT_ONBOARDING_FLOW)
        .describe('The screens and their order. Absent is the app’s own flow.'),
})
    .describe('Which lessons open each topic, which topics each goal suggests, and any reworded screen copy. A topic is ready only while one of its lessons is published; the first challenge is built in and needs no mapping.');
/**
 * A config as the database holds it, which may predate a built-in screen. Only for
 * reading stored rows: the wire still refuses a flow missing one.
 */
export const storedOnboardingConfigSchema = z.preprocess((body) => typeof body === 'object' &&
    body !== null &&
    'flow' in body &&
    Array.isArray(body.flow)
    ? { ...body, flow: withBuiltIns(body.flow) }
    : body, onboardingConfigSchema);
/**
 * The spec's goal-suggestion table, and no starts: which lessons open a topic is an
 * editorial choice ONB-10 makes, not one code can.
 */
export const DEFAULT_ONBOARDING_CONFIG = {
    starts: [],
    copy: [],
    flow: DEFAULT_ONBOARDING_FLOW,
    suggestions: [
        {
            goal: 'CURRENT_JOB',
            topics: ['COMMUNICATION', 'DECISIONS', 'PROJECT_MANAGEMENT'],
        },
        { goal: 'NEXT_ROLE', topics: ['CAREER', 'COMMUNICATION', 'LEADERSHIP'] },
        {
            goal: 'CAREER_CHANGE',
            topics: ['CAREER', 'BUSINESS_FUNDAMENTALS', 'COMMUNICATION'],
        },
        {
            goal: 'GROW_BUSINESS',
            topics: ['ENTREPRENEURSHIP', 'MARKETING', 'FINANCE'],
        },
        {
            goal: 'LEAD_PEOPLE',
            topics: ['TEAM_MANAGEMENT', 'LEADERSHIP', 'COMMUNICATION'],
        },
        {
            goal: 'UNDERSTAND_BUSINESS',
            topics: ['BUSINESS_FUNDAMENTALS', 'FINANCE', 'ECONOMICS'],
        },
        {
            goal: 'MONEY_DECISIONS',
            topics: ['PERSONAL_FINANCE', 'FINANCE', 'ECONOMICS'],
        },
        {
            goal: 'SOMETHING_ELSE',
            topics: ['BUSINESS_FUNDAMENTALS', 'DECISIONS', 'AI'],
        },
        { goal: 'EXPLORING', topics: ['BUSINESS_FUNDAMENTALS', 'DECISIONS', 'AI'] },
    ],
};
export const onboardingOptionsSchema = z
    .object({
    topics: z.array(z.object({
        topic: subjectSchema,
        ready: z
            .boolean()
            .describe('At least one of its opening lessons is published. False shows "Coming soon"; onboarding still starts.'),
    })),
    suggestions: z.array(goalSuggestionSchema),
    copy: onboardingCopySchema.describe('The live config’s rewordings of onboarding screens.'),
    flow: onboardingFlowSchema.describe('The live config’s screens, in order.'),
    version: z
        .int()
        .nullable()
        .describe('The live version, null for the built-in config.'),
})
    .describe('Every topic in onboarding order with whether it can be started, the goal-suggestion table, and reworded screen copy.');
export const challengeAnswerSchema = z
    .strictObject({ sceneId: idSchema, answer: sceneAnswerSchema })
    .describe('One question of the first challenge, answered by a guest. Graded and not stored: sign-in records the run.');
export const challengeVerdictSchema = verdictSchema
    .extend({
    strength: subjectSchema
        .nullable()
        .describe('The question’s topic when this answer was the strongest call, else null. The completion screen names up to three.'),
})
    .describe('The verdict every answer gets, and whether it names a strength.');
export const onboardingEventSchema = z
    .strictObject({
    event: z.enum([
        'CHALLENGE_STARTED',
        'CHALLENGE_LEFT',
        'CHALLENGE_COMPLETED',
    ]),
    sceneId: idSchema
        .optional()
        .describe('`CHALLENGE_LEFT` only: the scene on screen when the learner left.'),
})
    .describe('A guest’s step through the first challenge, counted as a metric. Each answer is counted where it is graded.');
export const onboardingConfigStatusSchema = z.enum(['DRAFT', 'PUBLISHED']);
export const onboardingConfigVersionSchema = z.object({
    version: z.int(),
    status: onboardingConfigStatusSchema,
    config: onboardingConfigSchema,
    publishedAt: z.iso.datetime().nullable(),
    restoredFrom: z
        .int()
        .nullable()
        .describe('The version a roll back copied, when this is one.'),
    updatedAt: z.iso.datetime(),
});
export const startProblemSchema = z
    .enum(['NO_NEXT_PUBLISHED'])
    .describe('Why a mapped topic cannot be started: none of its opening lessons is published.');
export const startCheckSchema = z.object({
    topic: subjectSchema,
    problem: startProblemSchema
        .nullable()
        .describe('Null when publishing this mapping makes the topic startable.'),
});
export const flowProblemSchema = z.object({
    step: z.string().describe('The step id the problem belongs to.'),
    level: z.enum(['block', 'warn']),
    message: z.string(),
});
export const onboardingChallengeSchema = z
    .object({
    lessonId: idSchema,
    title: z.string(),
    questions: z.array(z.object({ sceneId: idSchema, kind: z.string(), prompt: z.string() })),
})
    .describe('The shared first challenge as published: one lesson, its questions in order. Edited in the lesson editor.');
export const onboardingConfigsSchema = z
    .object({
    live: z
        .int()
        .nullable()
        .describe('The version learners get: the latest published. Null means the built-in config.'),
    versions: z
        .array(onboardingConfigVersionSchema)
        .describe('Newest first. At most one is a draft.'),
    editing: onboardingConfigSchema.describe('What an edit starts from: the draft, else the live config, else the built-in one.'),
    checks: z
        .array(startCheckSchema)
        .describe('Each of `editing`’s topic starts checked against content as it is now.'),
    problems: z
        .array(flowProblemSchema)
        .describe('`editing`’s flow checked as publishing would check it. A `block` stops publishing.'),
    challenge: onboardingChallengeSchema.nullable(),
})
    .describe('The onboarding config’s history.');
export const restoreOnboardingConfigSchema = z.strictObject({
    version: z
        .int()
        .positive()
        .describe('A published version to put live again.'),
});
export const onboardingCopyCatalogSchema = z
    .object({
    entries: z.array(z.object({
        key: z.string(),
        english: z
            .string()
            .describe('The app’s own words. `{{name}}` is a value the app fills, and a rewording must keep every one.'),
    })),
})
    .describe('Every onboarding string a config can reword, in screen order.');
//# sourceMappingURL=onboarding.js.map